/**
 * The Import module page's steps and the views they show. A view is named
 * by the page's `visning` query parameter; the first step's view has no
 * parameter.
 */

export type ImportView = 'kilde' | 'feltkoblinger' | 'kodeoppslag' | 'tomme-verdier' | 'listeverdier' | 'kontroll';

export type ImportStepKey = 'source' | 'mapping' | 'values' | 'run';

export interface ImportStep {
  key: ImportStepKey;
  labelKey: string;
  // The view the step opens on; a step with tabs has one view per tab.
  views: readonly ImportView[];
  // The title and intro over the step's view, the intro's keys rendered
  // in order as one paragraph.
  heading: {titleKey: string; introKeys: readonly string[]};
  // The footer's next button, named after the step it goes to, and the
  // line beside it saying what is done there. The last step has none.
  next?: {labelKey: string; hintKey: string};
}

/** The mapping step's tabs, in order. */
export const MAPPING_TABS: readonly {view: ImportView; labelKey: string}[] = [
  {view: 'feltkoblinger', labelKey: 'TRANS__IMPORT__STEPS__TAB_MAPPING'},
  {view: 'kodeoppslag', labelKey: 'TRANS__IMPORT__STEPS__TAB_LOOKUPS'},
  {view: 'tomme-verdier', labelKey: 'TRANS__IMPORT__STEPS__TAB_BLANK_VALUES'},
];

export const IMPORT_STEPS: readonly ImportStep[] = [
  {key: 'source', labelKey: 'TRANS__IMPORT__STEPS__SOURCE', views: ['kilde'],
   heading: {titleKey: 'TRANS__IMPORT__STEPS__SOURCE_TITLE',
             introKeys: ['TRANS__IMPORT__DESCRIPTION', 'TRANS__IMPORT__STEPS__SOURCE_INTRO']},
   next: {labelKey: 'TRANS__IMPORT__STEPS__TO_MAPPING', hintKey: 'TRANS__IMPORT__STEPS__SOURCE_NEXT_HINT'}},
  {key: 'mapping', labelKey: 'TRANS__IMPORT__STEPS__MAPPING',
   views: MAPPING_TABS.map(tab => tab.view),
   heading: {titleKey: 'TRANS__IMPORT__STEPS__MAPPING', introKeys: ['TRANS__IMPORT__STEPS__MAPPING_INTRO']},
   next: {labelKey: 'TRANS__IMPORT__STEPS__TO_VALUES', hintKey: 'TRANS__IMPORT__STEPS__MAPPING_NEXT_HINT'}},
  {key: 'values', labelKey: 'TRANS__IMPORT__STEPS__VALUES', views: ['listeverdier'],
   heading: {titleKey: 'TRANS__IMPORT__VALUE_REVIEW__HEADING', introKeys: ['TRANS__IMPORT__VALUE_REVIEW__DESCRIPTION']},
   next: {labelKey: 'TRANS__IMPORT__STEPS__TO_RUN', hintKey: 'TRANS__IMPORT__STEPS__VALUES_NEXT_HINT'}},
  {key: 'run', labelKey: 'TRANS__IMPORT__STEPS__RUN', views: ['kontroll'],
   heading: {titleKey: 'TRANS__IMPORT__STEPS__RUN', introKeys: ['TRANS__IMPORT__PREFLIGHT_HINT']}},
];

const FIRST_VIEW: ImportView = 'kilde';

/** Which steps past the first can be opened. Warnings never close one. */
export interface StepGates {
  // File, table, object type and the required batch defaults are set.
  mapping: boolean;
  // The mapping step is open and holds at least one mapping.
  later: boolean;
}

/** The view a `visning` value names; no value, or one no view has, is the
 * first step's. */
export function viewOfParam(param: string | null): ImportView {
  return IMPORT_STEPS.flatMap(step => step.views).find(view => view === param && view !== FIRST_VIEW)
    ?? FIRST_VIEW;
}

/** The `visning` value that names `view`: none for the first step. */
export function paramOfView(view: ImportView): string | null {
  return view === FIRST_VIEW ? null : view;
}

export function stepOfView(view: ImportView): ImportStep {
  return IMPORT_STEPS.find(step => step.views.includes(view))!;
}

export function isStepOpen(step: ImportStepKey, gates: StepGates): boolean {
  switch (step) {
    case 'source':
      return true;
    case 'mapping':
      return gates.mapping;
    default:
      return gates.mapping && gates.later;
  }
}

/** How far the page has come with each step, for the step bar. */
export interface StepProgress {
  gates: StepGates;
  // Values the value review has left undecided; `null` before a scan.
  undecidedValues: number | null;
  // Rows the shown Forhåndskontroll warned about; `null` while none is
  // shown.
  warnedRows: number | null;
  // A run of this import has finished.
  runFinished: boolean;
}

/**
 * How the step bar draws a step: the one shown is `current`; one with
 * something left to look at - undecided values, rows with warnings - is
 * `attention`; one that is finished is `done`; any other, and every step
 * that cannot be opened, is `pending`.
 */
export type StepState = 'current' | 'done' | 'attention' | 'pending';

export function stepState(step: ImportStepKey, current: ImportStepKey, progress: StepProgress): StepState {
  if (step === current) {
    return 'current';
  }
  if (!isStepOpen(step, progress.gates)) {
    return 'pending';
  }
  switch (step) {
    case 'source':
      return progress.gates.mapping ? 'done' : 'pending';
    case 'mapping':
      return progress.gates.later ? 'done' : 'pending';
    case 'values':
      return progress.undecidedValues === null ? 'pending'
        : progress.undecidedValues > 0 ? 'attention' : 'done';
    case 'run':
      return progress.warnedRows ? 'attention' : progress.runFinished ? 'done' : 'pending';
  }
}

export function isViewOpen(view: ImportView, gates: StepGates): boolean {
  return isStepOpen(stepOfView(view).key, gates);
}

/** The view shown in place of one that cannot be opened: the mapping
 * step's first tab while that step is open, else the first step. */
export function fallbackView(gates: StepGates): ImportView {
  return gates.mapping ? MAPPING_TABS[0].view : FIRST_VIEW;
}

/** The first view of the step before `view`'s, `null` on the first step.
 * The value review goes back to the mapping step's first tab. */
export function previousView(view: ImportView): ImportView | null {
  const index = IMPORT_STEPS.indexOf(stepOfView(view));
  return index > 0 ? IMPORT_STEPS[index - 1].views[0] : null;
}

/** The first view of the step after `view`'s, `null` on the last step. */
export function nextView(view: ImportView): ImportView | null {
  const index = IMPORT_STEPS.indexOf(stepOfView(view));
  return index < IMPORT_STEPS.length - 1 ? IMPORT_STEPS[index + 1].views[0] : null;
}

/** A footer button: the view it opens and its label. */
export interface StepLink {
  view: ImportView;
  labelKey: string;
}

/** The footer's back: the view `previousView` names, labelled with its
 * step's name. */
export function backLink(view: ImportView): StepLink | null {
  const back = previousView(view);
  return back ? {view: back, labelKey: stepOfView(back).labelKey} : null;
}

/** The footer's next: the view `nextView` names, with the shown step's
 * button label and hint. */
export function nextLink(view: ImportView): (StepLink & {hintKey: string}) | null {
  const next = nextView(view);
  const footer = stepOfView(view).next;
  return next && footer ? {view: next, ...footer} : null;
}
